import React from "react";
import { Alert, Grid } from "@mui/material";
import moment from "moment";
import { Dialog } from "../../../../../../components/Dialogs/Dialog";
import { CustomTextField } from "../../../../../../components/formsComponents";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import { useDispatchWrapper } from "../../../../../../hooks";
import { useBillingCompanyContext } from "../../../../../../providers";
import { upsertLeadAsync } from "../../../../../../redux/leads/leadsActions";
import { ILead, IMultipleEvent } from "../../../../../../redux/leads/leads.types";
import { showMessage } from "../../../../../../redux/messages/messagesActions";
import { EventDetailsManager } from "../../../ViewLeadDeatils/EventDetailsManager";
import { BookedVenueDialog } from "../../../BookedVenueDialog";
import {
  buildEventLeadPayload,
  buildEventLeadsBasePayload,
  validateEventLeads,
} from "../../../../helpers/leadHelpers";
import { useEventsVenueAvailability } from "../../../../hooks/useEventsVenueAvailability";
import { useDefaultOngoingStatus } from "../../../../hooks/useDefaultOngoingStatus";

/** Remark sent with every lead created from the quotation tab. */
export const QUOTATION_LEAD_REMARK = "lead created through quotation";

const isDeadEvent = (event: IMultipleEvent) =>
  (event.actual_status || "").toUpperCase() === "DEAD" ||
  (event.status || "").toUpperCase() === "DEAD";

/**
 * A new (unsaved) event lead prefilled from an existing event: lead status,
 * event type, event date, event time and preferred venue are carried over,
 * everything else starts empty like a freshly added event row.
 */
const buildPrefilledEventLead = (
  source: IMultipleEvent,
  fallbackFollowupDate: string | null,
): IMultipleEvent => ({
  leads_uuid: null,
  // Lead status
  status: source.status || "ONGOING",
  actual_status: source.actual_status || "ONGOING",
  drive_status_uuid: source.drive_status_uuid || "",
  // Event type (+ the fields that belong to specific event types)
  event_type: source.event_type || "",
  event_uuid: source.event_uuid || "",
  other_event_name: source.other_event_name ?? null,
  payment_mode: source.payment_mode ?? null,
  // Event date
  function_date: source.function_date ?? null,
  function_month: source.function_month ?? null,
  show_month_picker: !source.function_date && !!source.function_month,
  // Event time
  event_time: source.event_time || "",
  event_time_uuid: source.event_time_uuid || "",
  // Preferred venue
  preferred_venue: source.preferred_venue ?? null,
  preferred_venue_uuid: source.preferred_venue_uuid || "",
  // Lead-level in the lead form (one follow-up date for all events)
  followup_date: source.followup_date || fallbackFollowupDate,
  booking_date: null,
  service: [],
  number_of_rooms: 0,
  number_of_guests: 0,
  cancellation_dead_reason: null,
  customer_relative_name: null,
  relation_with_customer: null,
  estimated_rough_revenue: null,
  guest_budget: null,
  duplicate_reference_uuid: null,
  duplicate_reference_contact_number: null,
  is_archived: 0,
  extras: {},
});

interface ICreateEventLeadsDialogProps {
  open: boolean;
  /** Lead whose common (customer) fields every new event lead inherits — incl. its multiple_events_uuid. */
  baseLead: ILead;
  /** Events to prefill from; one new lead is created per entry. */
  sourceEvents: IMultipleEvent[];
  /** Let the user add/remove event rows in the dialog to create several leads at once. */
  allowMultiple?: boolean;
  remarks?: string;
  onClose: () => void;
  onSuccess: (createdLeads: ILead[]) => void;
}

export const CreateEventLeadsDialog: React.FC<ICreateEventLeadsDialogProps> = ({
  open,
  baseLead,
  sourceEvents,
  allowMultiple = false,
  remarks = QUOTATION_LEAD_REMARK,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const defaultOngoingStatus = useDefaultOngoingStatus();

  const [events, setEvents] = React.useState<IMultipleEvent[]>(() =>
    sourceEvents.map((source) =>
      buildPrefilledEventLead(source, baseLead.followup_date || null),
    ),
  );
  const [followupDate, setFollowupDate] = React.useState<string | null>(
    () => events.find((e) => e.followup_date)?.followup_date || null,
  );
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const { availabilityMap, hasUnavailableVenue, dismissBookingDetails } =
    useEventsVenueAvailability(events, { checkInitialEvents: true });

  // Source events without a status get the outlet's default ONGOING status
  React.useEffect(() => {
    if (!defaultOngoingStatus) return;
    setEvents((prev) =>
      prev.map((event) =>
        event.actual_status === "ONGOING" && !event.drive_status_uuid
          ? {
            ...event,
            status: defaultOngoingStatus.name || event.status,
            drive_status_uuid: defaultOngoingStatus.status_uuid || "",
          }
          : event,
      ),
    );
  }, [defaultOngoingStatus]);

  const errors = React.useMemo(() => validateEventLeads(events), [events]);
  const isDeadStatus = events.some(isDeadEvent);
  const followupDateError =
    submitAttempted && !isDeadStatus
      ? events
        .map((_, index) => errors[`multiple_events_list_${index}_followup_date`])
        .find(Boolean) || ""
      : "";

  // Same follow-up date sync as the lead form: DEAD events never keep one
  const handleEventsChange = (updatedEvents: IMultipleEvent[]) => {
    const hasDead = updatedEvents.some(isDeadEvent);
    setEvents(
      updatedEvents.map((event) => ({
        ...event,
        followup_date: isDeadEvent(event)
          ? null
          : event.followup_date || followupDate,
      })),
    );
    if (hasDead) setFollowupDate(null);
  };

  const handleFollowupDateChange = (newValue: string | null) => {
    setFollowupDate(newValue);
    setEvents((prev) => prev.map((event) => ({ ...event, followup_date: newValue })));
  };

  const handleConfirm = () => {
    setSubmitAttempted(true);
    const errorMessages = Array.from(new Set(Object.values(errors)));
    if (errorMessages.length > 0) {
      dispatch(
        showMessage({
          type: "error",
          message: errorMessages.join(", "),
          displayAs: "snackbar",
        }),
      );
      return;
    }

    const baseValues = {
      ...buildEventLeadsBasePayload(baseLead, companyOutlet.comapny),
      // The new leads join the existing enquiry, so it always has several events
      multiple_events: 1,
      remarks,
    };
    const payload = events.map((event) => buildEventLeadPayload(baseValues, event));

    setSaving(true);
    dispatch(
      upsertLeadAsync(payload, baseLead, false, false, (isSuccess, data) => {
        setSaving(false);
        if (isSuccess) {
          onSuccess(Array.isArray(data) ? data : data ? [data] : []);
        }
      }),
    );
  };

  return (
    <>
      <Dialog
        open={open}
        size="xl"
        title={allowMultiple ? "Create Leads from Event" : "Create Lead from Event"}
        subtitle={[baseLead.name_of_guest, baseLead.contact_number_one]
          .filter(Boolean)
          .join(" · ")}
        onClose={() => !saving && onClose()}
        actions={[
          {
            type: "button",
            variant: "outlined",
            label: "Cancel",
            disabled: saving,
            onClick: onClose,
          },
          {
            type: "button",
            variant: "contained",
            label: saving ? "Saving..." : "Confirm",
            disabled: saving || events.length === 0 || hasUnavailableVenue,
            onClick: handleConfirm,
          },
        ]}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          The event details below are copied from the selected event. Review or
          change them, then confirm to create a new lead in this enquiry.
        </Alert>

        <EventDetailsManager
          events={events}
          initialEvents={[]}
          onEventsChange={handleEventsChange}
          errors={errors}
          submitAttempted={submitAttempted}
          companyOutletUuid={companyOutlet.comapny.company_outlet_uuid as string}
          isEventDisabled={() => saving}
          disableFormByStatus={saving}
          loading={saving}
          hideAddActionButton={!allowMultiple}
          eventAvailabilityMap={availabilityMap}
          leadData={baseLead}
          defaultExpandedIndex={0}
          isInsideDialog
        />

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {!isDeadStatus && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <CustomDatePicker
                placeholder="Followup Date"
                fullWidth
                minDate={moment()}
                value={followupDate || ""}
                disabled={saving}
                onChange={handleFollowupDateChange}
                errorMessage={followupDateError}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={8} lg={9}>
            <CustomTextField
              label="Remark"
              name="remarks"
              value={remarks}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
      </Dialog>

      {Object.entries(availabilityMap).map(([index, availability]) =>
        availability.checked &&
          availability.details &&
          availability.availabilty === false ? (
          <BookedVenueDialog
            key={index}
            open={true}
            onClose={() => dismissBookingDetails(Number(index))}
            BookingData={availability.details}
          />
        ) : null,
      )}
    </>
  );
};
